import { TooltipWrapper } from 'components/TooltipWrapper'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { ConsequenceMatrixConfig, ConsequenceMatrixSelection } from 'types'

interface ConsequenceMatrixSelectorProps {
  config: ConsequenceMatrixConfig
  selections: ConsequenceMatrixSelection[]
  onSelectionChange: (selections: ConsequenceMatrixSelection[]) => void
  readOnly?: boolean
  userId: string
  userName: string
  /** Consequence row value to display when no categories are selected (e.g. the
   *  risk was set directly from the risk matrix). */
  fallbackConsequence?: number | null
}

const Container = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  background: #363636;
`

const Table = styled.table`
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 1px 4px;
  background: #2a2a2a;
`

const Th = styled.th`
  background: #404040;
  color: #fff;
  font-weight: 600;
  font-size: 12px;
  padding: 8px 12px;
  text-align: center;
  white-space: nowrap;
  border: none;
  min-height: 32px;
`

const RowLabelTh = styled(Th)`
  position: sticky;
  left: 0;
  z-index: 1;
`

const Td = styled.td<{
  $selected?: boolean
  $readOnly?: boolean
}>`
  font-size: 11px;
  color: #ccc;
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
  border: none;
  background: ${({ $selected, theme }) =>
    $selected ? `${theme.forecast.primary}25` : '#363636'};
  ${({ $selected, theme }) =>
    $selected && `box-shadow: inset 0 0 0 2px ${theme.forecast.primary};`}
  height: 42px;
  max-height: 42px;
  overflow: hidden;
  vertical-align: middle;
  padding: 4px 8px;
  min-width: 100px;
  max-width: 180px;

  ${({ $readOnly, theme }) =>
    !$readOnly &&
    `
    &:hover {
      background: ${theme.forecast.primary}15;
    }
  `}
`

const RowLabelTd = styled.td`
  position: sticky;
  left: 0;
  z-index: 1;
  background: #404040;
  color: #fff;
  font-weight: 600;
  font-size: 12px;
  padding: 8px 12px;
  text-align: center;
  border: none;
  white-space: nowrap;
  height: 42px;
`

const CellText = styled.div`
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const ConsequenceRow = styled.div`
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  border-top: 1px solid #2a2a2a;
`

const TooltipContent = styled.span`
  display: block;
  font-size: 12px;
  line-height: 1.4;
  max-width: 280px;
`

const TooltipSeparator = styled.span`
  display: block;
  border-top: 1px solid ${({ theme }) => theme.forecast.secondaryText}50;
  margin: 6px 0;
`

const TooltipMeta = styled.span`
  display: block;
  font-size: 11px;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

export const ConsequenceMatrixSelector: React.FC<
  ConsequenceMatrixSelectorProps
> = ({
  config,
  selections,
  onSelectionChange,
  readOnly = false,
  userId,
  userName,
  fallbackConsequence = null,
}) => {
  const selectionByColumn = useMemo(
    () => new Map(selections.map((s) => [s.columnIndex, s])),
    [selections]
  )

  const consequence = useMemo(() => {
    if (selections.length === 0) return fallbackConsequence
    return Math.max(...selections.map((s) => s.selectedRowValue))
  }, [selections, fallbackConsequence])

  const handleCellClick = useCallback(
    (rowIndex: number, colIndex: number) => {
      if (readOnly) return

      const existing = selectionByColumn.get(colIndex)
      if (existing?.selectedRowValue === config.rowLabels[rowIndex]) {
        onSelectionChange(selections.filter((s) => s.columnIndex !== colIndex))
        return
      }

      const newSelection: ConsequenceMatrixSelection = {
        columnIndex: colIndex,
        columnTitle: config.titles[colIndex],
        selectedRowValue: config.rowLabels[rowIndex],
        userId,
        userName,
        timestamp: Date.now(),
      }

      const updated = selections.filter((s) => s.columnIndex !== colIndex)
      updated.push(newSelection)
      onSelectionChange(updated)
    },
    [
      readOnly,
      config,
      selections,
      selectionByColumn,
      onSelectionChange,
      userId,
      userName,
    ]
  )

  return (
    <Container>
      <Table>
        <thead>
          <tr>
            <RowLabelTh />
            {config.titles.map((title, i) => (
              <Th key={i}>{title}</Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {config.rowLabels.map((label, rowIndex) => (
            <tr key={label}>
              <RowLabelTd>{label}</RowLabelTd>
              {config.titles.map((_, colIndex) => {
                const selection = selectionByColumn.get(colIndex)
                const isSelected = selection?.selectedRowValue === label
                const cellText = config.matrix[rowIndex]?.[colIndex] ?? ''
                const tooltipLabel = cellText ? (
                  <TooltipContent>
                    <span>{cellText}</span>
                    {isSelected && selection && (
                      <>
                        <TooltipSeparator />
                        <TooltipMeta>
                          {selection.userName} &middot;{' '}
                          {new Date(selection.timestamp).toLocaleDateString()}
                        </TooltipMeta>
                      </>
                    )}
                  </TooltipContent>
                ) : null
                return (
                  <Td
                    key={colIndex}
                    $selected={isSelected}
                    $readOnly={readOnly}
                    onClick={() => handleCellClick(rowIndex, colIndex)}>
                    {tooltipLabel ? (
                      <TooltipWrapper
                        label={tooltipLabel}
                        side='top'
                        offset={8}
                        zIndex={100}>
                        <CellText>{cellText}</CellText>
                      </TooltipWrapper>
                    ) : (
                      <CellText>{cellText}</CellText>
                    )}
                  </Td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </Table>
      <ConsequenceRow>
        Consequence: {consequence !== null ? consequence : '--'}
      </ConsequenceRow>
    </Container>
  )
}
